import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack,
  PictureAsPdf,
  Person,
  Event,
  Store,
  Description,
  Email,
  CheckCircle,
  Schedule,
  Gavel,
  VerifiedUser,
  Warning,
  ContentCopy
} from '@mui/icons-material';
import { UseApi } from '../Hooks/UseApi';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';

const RecordPacketPage = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const api = UseApi();
  const { getSubscriptionLevel, isStudioOwner } = UseAuthRoles();
  
  // Prevent infinite loops
  const hasFetched = useRef(false);
  
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [packetData, setPacketData] = useState(null);
  
  const subscriptionLevel = getSubscriptionLevel();
  const canAccess = subscriptionLevel === 'growth_studio' && isStudioOwner();
  
  // Fetch record packet data - only once
  useEffect(() => {
    if (hasFetched.current) return;
    
    if (!canAccess) {
      setError('Access denied. Record Packets are only available for Growth Studio owners.');
      setLoading(false);
      return;
    }
    
    const fetchRecordPacket = async () => {
      hasFetched.current = true;
      setLoading(true);
      setError(null);
      
      try {
        const data = await api.fetchWithAuth(`/appointments/${appointmentId}/record-packet/`);
        setPacketData(data);
      } catch (err) {
        console.error('Error fetching record packet:', err);
        setError(err.message || 'Failed to load record packet data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecordPacket();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]); // Only re-fetch if appointmentId changes
  
  // Handle PDF download
  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    setError(null);
    
    try {
      // Get token from Auth0
      const token = await getAccessTokenSilently({
        audience: "https://inkedupmanager.com/api",
      });
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || 'https://api.inkedupmanager.com'}/appointments/${appointmentId}/record-packet/pdf/`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Create filename from client name and date
        const clientName = packetData?.client 
          ? `${packetData.client.first_name}_${packetData.client.last_name}`.replace(/\s+/g, '_')
          : 'Unknown';
        const date = packetData?.appointment?.date || 'NoDate';
        link.setAttribute('download', `RecordPacket_${clientName}_${date}.pdf`);
        
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        setSuccess('PDF downloaded successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to download PDF');
      }
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError(err.message || 'Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };
  
  // Copy Record ID to clipboard
  const handleCopyRecordId = () => {
    if (packetData?.appointment?.record_packet_id) {
      navigator.clipboard.writeText(packetData.appointment.record_packet_id);
      setSuccess('Record ID copied to clipboard!');
      setTimeout(() => setSuccess(null), 2000);
    }
  };
  
  // Format datetime for display
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Format date only
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Access denied state
  if (!canAccess) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Warning sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Access Restricted
          </Typography>
          <Typography color="text.secondary" paragraph>
            Record Packets are only available for Growth Studio owners.
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/appointments')}
          >
            Back to Appointments
          </Button>
        </Box>
      </Container>
    );
  }
  
  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  // Error state (no data)
  if (error && !packetData) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/record-packets')}
          >
            Back to Record Packets
          </Button>
        </Box>
      </Container>
    );
  }
  
  const { appointment, client, artist, shop, consent_forms, legacy_consent_forms } = packetData || {};
  const allConsentForms = [...(consent_forms || []), ...(legacy_consent_forms || [])];
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            component="button"
            underline="hover" 
            color="inherit" 
            onClick={() => navigate('/record-packets')}
          >
            Record Packets
          </Link>
          <Typography color="text.primary">
            {client?.first_name} {client?.last_name} - {formatDate(appointment?.date)}
          </Typography>
        </Breadcrumbs>
        
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              <Gavel sx={{ mr: 1, verticalAlign: 'bottom' }} />
              Record Packet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete documentation for this appointment
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/record-packets')}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={pdfLoading ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
              onClick={handleDownloadPDF}
              disabled={pdfLoading}
            >
              {pdfLoading ? 'Generating...' : 'Download PDF'}
            </Button>
          </Box>
        </Box>
        
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        {/* Record Packet ID */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                Immutable Record Packet ID
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                {appointment?.record_packet_id || 'N/A'}
              </Typography>
            </Box>
            <Tooltip title="Copy Record ID">
              <IconButton onClick={handleCopyRecordId}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary">
            This ID is permanent and cannot be changed. Use it to reference this record in legal proceedings.
          </Typography>
        </Paper>
        
        <Grid container spacing={3}>
          {/* Appointment Details */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Event sx={{ mr: 1, verticalAlign: 'bottom' }} />
                  Appointment Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Date</Typography>
                    <Typography variant="body1">{formatDate(appointment?.date)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Time</Typography>
                    <Typography variant="body1">{appointment?.time || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Duration</Typography>
                    <Typography variant="body1">{appointment?.duration || 60} minutes</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Type</Typography>
                    <Chip 
                      label={appointment?.is_walk_in ? 'Walk-in' : 'Scheduled'}
                      size="small"
                      color={appointment?.is_walk_in ? 'info' : 'success'}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Confirmation Status</Typography>
                    {appointment?.confirmed ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <CheckCircle color="success" fontSize="small" />
                        <Typography variant="body2" color="success.main">
                          Confirmed at {formatDateTime(appointment?.confirmed_at)}
                        </Typography>
                      </Box>
                    ) : (
                      <Chip label="Not Confirmed" size="small" color="default" />
                    )}
                  </Grid>
                  {appointment?.notes && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Notes</Typography>
                      <Typography variant="body1">{appointment.notes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Client Information */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Person sx={{ mr: 1, verticalAlign: 'bottom' }} />
                  Client Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {client?.first_name} {client?.last_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{client?.email || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{client?.phone_number || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                    <Typography variant="body1">{client?.date_of_birth || 'N/A'}</Typography>
                  </Grid>
                  {client?.address && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Address</Typography>
                      <Typography variant="body1">
                        {client.address}
                        {client.city && `, ${client.city}`}
                        {client.state && `, ${client.state}`}
                        {client.zip_code && ` ${client.zip_code}`}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Studio & Artist */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Store sx={{ mr: 1, verticalAlign: 'bottom' }} />
                  Studio & Artist
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {shop && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Studio</Typography>
                        <Typography variant="body1" fontWeight="bold">{shop.name || 'N/A'}</Typography>
                      </Grid>
                      {shop.address && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">Address</Typography>
                          <Typography variant="body1">{shop.address}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                  {artist && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Artist</Typography>
                        <Typography variant="body1">
                          {artist.first_name} {artist.last_name}
                          {artist.role && <Chip label={artist.role} size="small" sx={{ ml: 1 }} />}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Email Communication */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Email sx={{ mr: 1, verticalAlign: 'bottom' }} />
                  Email Communication Log
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Sent At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Confirmation</TableCell>
                        <TableCell>
                          {appointment?.confirmation_email_sent ? (
                            <Chip label="Sent" size="small" color="success" />
                          ) : (
                            <Chip label="Not Sent" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(appointment?.confirmation_email_sent_at)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Reminder</TableCell>
                        <TableCell>
                          {appointment?.reminder_email_sent ? (
                            <Chip label="Sent" size="small" color="success" />
                          ) : (
                            <Chip label="Not Sent" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(appointment?.reminder_email_sent_at)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Consent Forms */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Description sx={{ mr: 1, verticalAlign: 'bottom' }} />
                  Consent Documentation
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {allConsentForms.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Form</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Date Signed</TableCell>
                          <TableCell>Signature</TableCell>
                          <TableCell>Electronic Consent</TableCell>
                          <TableCell>IP Address</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allConsentForms.map((form, index) => (
                          <TableRow key={form.id || index}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {form.template_title || form.form_type || 'Consent Form'}
                              </Typography>
                              {form.match_type === 'date_proximity' && (
                                <Typography variant="caption" color="warning.main">
                                  (Matched by date)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={form.type === 'custom_form' ? 'Custom Form' : 'Legacy'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {formatDateTime(form.signature_timestamp || form.date_signed)}
                            </TableCell>
                            <TableCell>
                              {form.signature ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <VerifiedUser color="success" fontSize="small" />
                                  <Typography variant="body2" color="success.main">Signed</Typography>
                                </Box>
                              ) : (
                                <Chip label="Not Signed" size="small" color="warning" />
                              )}
                            </TableCell>
                            <TableCell>
                              {form.consent_to_electronic ? (
                                <CheckCircle color="success" fontSize="small" />
                              ) : (
                                <Typography variant="body2" color="text.secondary">N/A</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {form.ip_address || 'N/A'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Warning sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No Consent Forms Linked
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No consent forms are directly linked to this appointment.
                      Forms may exist but were created before appointment linking was enabled.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Legal Notice */}
        <Paper elevation={0} sx={{ p: 2, mt: 3, bgcolor: 'grey.100' }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Legal Notice:</strong> This Record Packet contains all documented interactions and consent forms 
            associated with this appointment. The Record Packet ID is immutable and serves as a permanent 
            reference. All timestamps are recorded in the server's timezone 
            and reflect the actual time of documentation.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default RecordPacketPage;